import jsonStringifySafe from 'json-stringify-safe-es';

/**
 * 对象值转换函数
 * @description 输入一个对象中的某一对 key 和 value, 返回新的 value
 */
export type ObjectPairTransformFunction = (key: any, value: any) => any;

/**
 * 同 JSON.parse，当传入可解析的 JSON 时返回 JSON 的解析结果
 *
 * 但当 JSON 解析失败时，用以下逻辑兜底：
 * - 没有 fallback 参数时直接返回 undefined
 * - fallback 为函数时，调用该函数，返回其返回的内容（fallback 函数抛错时 parseJson 也会抛错）
 * - 其它情况直接返回 fallback 作为兜底值
 *
 * 要注意的是，parseJson 不同于 JSON.parse，它默认返回 unknown 类型而非 any 类型，大家尽量给解析后的对象增加类型定义，而不是用 any 就算了
 *
 * @example
 * ```js
 * // 传入可解析的 JSON
 * parseJson("{}");                 // -> {}
 *
 * // 传入无效的 JSON 且没有 fallback
 * parseJson("");                   // -> undefined
 *
 * // 传入无效的 JSON 和 fallback 函数
 * parseJson("", () => "failed");   // -> failed
 *
 * // 传入无效的 JSON 和 fallback 值
 * parseJson("", -1);               // -> -1
 *
 * // 默认返回 unknown 类型
 * const a = parseJson("{}");       // a: unknown
 *
 * // 显式声明解析后的类型
 * const b = parseJson<T>("{}");    // b: T | undefined
 * const c = parseJson<any>("{}");  // c: any
 * ```
 */
export function parseJson<T>(json: string): T | undefined;
export function parseJson<T>(json: string, fallback: T | (() => T)): T;
export function parseJson(json: string, fallback?: any): any {
  try {
    return JSON.parse(json);
  } catch {
    if (typeof fallback === 'function') return fallback();
    return fallback;
  }
}

/**
 * 同 parseJson，但是支持 reviver 参数
 * @description 注：考虑到平时 reviver 用的少，所以单独开一个函数
 * @see [[parseJson]]
 */
export function parseJsonWithReviver<T>(json: string, reviver: ObjectPairTransformFunction): T | undefined;
export function parseJsonWithReviver<T>(json: string, reviver: ObjectPairTransformFunction, fallback: T | (() => T)): T;
export function parseJsonWithReviver(json: string, reviver: ObjectPairTransformFunction, fallback?: any): any {
  try {
    return JSON.parse(json, reviver);
  } catch {
    if (typeof fallback === 'function') return fallback();
    return fallback;
  }
}

/**
 * 同 JSON.stringify，但在遇到循环引用时会把循环引用转为 "[Circular ~.*]" 格式，不会像原生 JSON.stringify 一样直接抛错
 * @returns 返回 JSON 字符串
 * @description 此函数来自第三方库 [json-stringify-safe](https://github.com/moll/json-stringify-safe) ([source](https://github.com/moll/json-stringify-safe/blob/master/stringify.js))
 * @example
 * ```js
 * var circularObj = {};
 * circularObj.circularRef = circularObj;
 * circularObj.list = [circularObj, circularObj];
 * stringifyJson(circularObj);    // `{"circularRef":"[Circular ~]","list":["[Circular ~]","[Circular ~]"]}`
 * ```
 */
export const stringifyJson: (
  obj: any,
  serializer?: ObjectPairTransformFunction,
  indent?: number,
  decycler?: ObjectPairTransformFunction,
) => string = jsonStringifySafe;
